import Footer from "./Footer";
import FormElement from "./FormElement";
import Header from "./Header";
import * as support from "./Support";
import {
  ChevronLeftRounded as BackIcon,
  ArrowForwardRounded as ForwardIcon,
  CloseRounded as CloseIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { hasItems } from "common/arrays";
import DefaultLoader from "components/common/DefaultLoader";
import IconButton from "components/common/IconButton";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import * as bffService from "services/bffService";
import * as staticDataService from "services/staticDataService";

export const SECTION_NAVIGATION = {
  STEPWISE: "STEPWISE",
  STACK: "STACK",
  COLUMNS: "COLUMNS",
};

export const FORM_ELEMENT = {
  TEXT: "TEXT",
  NUMBER: "NUMBER",
  DATE: "DATE",
  CHECKBOX: "CHECKBOX",
  LABEL: "LABEL",
  EXPOSURE_FLAG: "EXPOSURE_FLAG",
  INDUSTRY_CLASS: "INDUSTRY_CLASS",
  US_STATE: "US_STATE",
  PRODUCER: "PRODUCER",
  PRICING_DEFAULTS: "PRICING_DEFAULTS",
  LAYERS: "LAYERS",
  SUBJECTIVITIES: "SUBJECTIVITIES",
  CONTENT: "CONTENT",
  SUMMARY: "SUMMARY",
  SUBMIT_BUTTON: "SUBMIT_BUTTON",
};

const RULES = {
  LOOKUP_INDUSTRY_LABEL: "LOOKUP_INDUSTRY_LABEL",
  UPDATE_IF_BLANK: "UPDATE_IF_BLANK",
  HIDE_IF_NULL: "HIDE_IF_NULL",
};

const createConfig = (theme) => ({
  title: "New Submission",
  background: theme.brand.gray[500],
  color: "black",
  sectionNavigation: SECTION_NAVIGATION.STACK,
  sections: [
    {
      title: "Insured Details",
      description:
        "Please provide basic details about the entity seeking cover",
      items: [
        {
          label: "Applicant Name",
          type: FORM_ELEMENT.TEXT,
          key: "applicant",
        },
        {
          label: "Insured Address",
          type: FORM_ELEMENT.TEXT,
          key: "address",
          multiline: true,
        },
        {
          label: "State",
          type: FORM_ELEMENT.US_STATE,
          key: "state",
        },
        {
          label: "Annual Revenue",
          type: FORM_ELEMENT.NUMBER,
          key: "revenue",
          prefix: "$",
        },
      ],
    },
    {
      title: "Risk Details",
      description: "Please provide some background on the customer's business",
      items: [
        {
          label: "Professional Service",
          type: FORM_ELEMENT.INDUSTRY_CLASS,
          key: "service",
          rules: [
            {
              type: RULES.LOOKUP_INDUSTRY_LABEL,
            },
            {
              type: RULES.UPDATE_IF_BLANK,
              key: "summary",
              useLatestValue: true,
            },
          ],
        },
        {
          label: "Service Wording (Optional)",
          type: FORM_ELEMENT.TEXT,
          key: "summary",
          multiline: true,
        },
        {
          label: "Experience",
          type: FORM_ELEMENT.EXPOSURE_FLAG,
          key: "experience",
          exposureFlag: "EXPERIENCE",
        },
        {
          label: "Claims",
          type: FORM_ELEMENT.EXPOSURE_FLAG,
          key: "claims",
          exposureFlag: "CLAIMS",
        },
      ],
    },
    {
      title: "Coverage Sought",
      description: "What type of coverage is the insured seeking?",
      items: [
        {
          label: "Inception",
          type: FORM_ELEMENT.DATE,
          key: "inception",
        },
        {
          label: "To be determined",
          type: FORM_ELEMENT.CHECKBOX,
          key: "inceptionTbd",
          exposureFlag: "INCEPTION_TBD",
        },
        {
          label: "Retroactive Date",
          type: FORM_ELEMENT.EXPOSURE_FLAG,
          key: "retro",
          exposureFlag: "RETRO",
        },
        {
          label: "Proposed Options",
          type: FORM_ELEMENT.LAYERS,
          key: "layers",
        },
      ],
    },
    {
      title: "Subjectivities",
      description: "Prerequisites for the policy",
      items: [
        {
          type: FORM_ELEMENT.SUBJECTIVITIES,
        },
      ],
    },
    {
      title: "Summary",
      description:
        "Please confirm you are happy with all the details before proceeding",
      items: [
        {
          label: "Producer",
          type: FORM_ELEMENT.PRODUCER,
          key: "producer",
        },
        {
          type: FORM_ELEMENT.SUMMARY,
          items: [
            {
              label: "Insured",
              key: "applicant",
            },
            {
              label: "Revenue",
              key: "revenue",
              format: "currency",
            },
            {
              format: "layers",
            },
          ],
        },
        {
          label: "Submit",
          type: FORM_ELEMENT.SUBMIT_BUTTON,
        },
      ],
    },
  ],
});

const BackButton = ({ onClick }) => (
  <Button
    onClick={onClick}
    variant={"text"}
    sx={{
      alignSelf: "flex-start",
      marginLeft: "-1rem",
      marginBottom: "1rem",
      paddingRight: "1rem",
      color: "rgba(0, 0, 0, 0.6)",
    }}
  >
    {<BackIcon />}
    {"Back"}
  </Button>
);

const ForwardButton = ({ onClick }) => (
  <Button
    onClick={onClick}
    variant={"contained"}
    sx={{ alignSelf: "flex-end", marginTop: "1.5rem", paddingLeft: "0.75rem" }}
    disableElevation
  >
    {"Next"}
    {<ForwardIcon fontSize={"small"} sx={{ marginLeft: "0.375rem" }} />}
  </Button>
);

const Sections = ({ config }) => {
  const navigate = useNavigate();

  const { formId } = useParams();

  const { data: form, isLoading } = bffService.useRetrieveFormQuery(
    { formId },
    {
      skip: formId == null,
      refetchOnMountOrArgChange: true,
    }
  );

  const { data: endorsementsConfig } = staticDataService.useConfigQuery(
    "endorsements"
  );

  const [formData, setFormData] = useState(support.EMPTY_FORM);

  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMissingName, setShowMissingName] = useState(false);
  const [validateForm, setValidateForm] = useState(support.VALID_FORM);
  const [currentSection, setCurrentSection] = useState(0);

  useEffect(() => {
    if (form != null) {
      setFormData(support.applyFormDefaults(form.formData, endorsementsConfig));
    } else if (endorsementsConfig) {
      setFormData((current) => {
        return current.subjectivities == null
          ? support.applyFormDefaults(current, endorsementsConfig)
          : current;
      });
    }
  }, [form, endorsementsConfig]);

  const visibleSections = config.sections.filter((section) => {
    let visible = true;
    if (hasItems(section.rules)) {
      for (let i = 0; i < section.rules.length; i++) {
        const rule = section.rules[i];
        if (rule.type === RULES.HIDE_IF_NULL && formData[rule.key] == null) {
          visible = false;
          break;
        }
      }
    }
    return visible;
  });

  const totalSections = visibleSections.length;
  const stepwiseMode = config.sectionNavigation === SECTION_NAVIGATION.STEPWISE;
  const backVisible = stepwiseMode && currentSection > 0;
  const forwardVisible = stepwiseMode && currentSection < totalSections - 1;
  const sectionsToDisplay = visibleSections.filter((_, index) => {
    return !stepwiseMode || index === currentSection;
  });

  const industryClasses = useSelector(
    (state) => state.temp?.exposureMetrics?.industryClassChoices ?? []
  );

  const changeSection = (section) => {
    setShowMissingName(false);
    setValidateForm(support.VALID_FORM);
    setCurrentSection(section);
  };

  const [createForm] = bffService.useCreateFormMutation();
  const [updateForm] = bffService.useUpdateFormMutation();
  const [submitForm] = bffService.useSubmitFormMutation();
  const [resubmitForm] = bffService.useResubmitFormMutation();

  const createOrUpdateForm = async () => {
    let id;
    if (formId) {
      await updateForm({ formId, formData });
    } else {
      const result = await createForm({ formData });
      id = result?.data?.id;
    }
    return formId ?? id;
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (formData.applicant == null) {
        changeSection(0);
        window.scrollTo(0, 0);
        setShowMissingName(true);
        setTimeout(() => document.body.querySelector("input")?.focus(), 75);
      } else {
        setShowMissingName(false);
        await createOrUpdateForm();
        navigate("/forms");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      setShowMissingName(false);
      const validateForm = support.validateForm(formData);
      setValidateForm(validateForm);
      let id;
      if (validateForm.isValid) {
        if (form?.isSubmitted) {
          await createOrUpdateForm({ skipNavigation: true });
          await resubmitForm({ formId });
        } else {
          id = await createOrUpdateForm({ skipNavigation: true });
          await submitForm({ formId: formId ?? id });
        }
        navigate(`/forms/${formId ?? id}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (value, config) => {
    if (config.type === FORM_ELEMENT.SUBMIT_BUTTON) {
      handleSubmit();
    } else if (value != null && config.type === FORM_ELEMENT.PRICING_DEFAULTS) {
      setFormData({ ...formData, ...value });
    } else if (value != null && config.type === FORM_ELEMENT.SUBJECTIVITIES) {
      setFormData({ ...formData, subjectivities: value });
    } else {
      const updated = {
        ...formData,
        [config.key]: value === "" ? null : value,
      };
      if (hasItems(config.rules)) {
        let latestValue = value;
        config.rules.forEach((rule) => {
          if (rule.type === RULES.LOOKUP_INDUSTRY_LABEL) {
            latestValue =
              industryClasses.find((x) => x.key === value)?.name ?? null;
          } else if (rule.type === RULES.UPDATE_IF_BLANK) {
            const ruleValue = rule.useLatestValue ? latestValue : value;
            if ((updated[rule.key] ?? "").trim() === "") {
              updated[rule.key] = ruleValue === "" ? null : ruleValue;
            }
          }
        });
      }
      setFormData(updated);
    }
  };

  const handleClose = () => {
    navigate("/forms");
  };

  if (
    form?.isSubmitted &&
    ["BOUND", "NOT_TAKEN_UP", "DECLINED_TO_QUOTE"].includes(form?.status)
  ) {
    setTimeout(() => navigate(`/forms/${formId}`), 0);
  }

  return (
    <>
      <Header
        config={config}
        ActionItems={
          <Stack direction={"row"} gap={1}>
            {!form?.isSubmitted && (
              <LoadingButton
                onClick={handleSave}
                size={"small"}
                variant={"contained"}
                disableElevation
                loading={isSaving}
              >
                {"Save for Later"}
              </LoadingButton>
            )}
            <IconButton
              icon={formId == null ? DeleteIcon : CloseIcon}
              tooltip={
                formId == null ? "Abandon submission" : "Close without saving"
              }
              onClick={handleClose}
            />
          </Stack>
        }
      />
      <div style={{ minHeight: "calc(100vh - 14.5rem)" }}>
        {isLoading ? (
          <DefaultLoader
            size={100}
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          />
        ) : (
          sectionsToDisplay.map((section, sectionIndex) => (
            <Stack
              sx={{
                maxWidth: "40rem",
                margin:
                  !stepwiseMode && sectionIndex > 0 ? "5rem auto" : "0 auto",
              }}
              key={sectionIndex}
              className={"fade-in-slow"}
            >
              {backVisible ? (
                <BackButton onClick={() => changeSection(currentSection - 1)} />
              ) : (
                <Box sx={{ height: "52px" }} />
              )}
              <Typography variant={"h5"}>{section.title}</Typography>
              {section.description && (
                <Typography
                  marginBottom={3}
                  color={"lightslategrey !important"}
                >
                  {section.description}
                </Typography>
              )}
              {showMissingName && (
                <Typography className={"fade-in-slow"}>
                  {"Please provide a name for the submission"}
                </Typography>
              )}
              {section.items.map((item, itemIndex) => (
                <FormElement
                  key={itemIndex}
                  form={form}
                  formData={formData}
                  config={item}
                  state={{
                    isSaving,
                    isSubmitting,
                  }}
                  onChange={(value) => handleChange(value, item)}
                />
              ))}
              {!validateForm.isValid &&
                sectionIndex === sectionsToDisplay.length - 1 && (
                  <Typography className={"fade-in-slow"} marginTop={3}>
                    {validateForm.message}
                  </Typography>
                )}
              {forwardVisible && (
                <ForwardButton
                  onClick={() => changeSection(currentSection + 1)}
                />
              )}
            </Stack>
          ))
        )}
      </div>
      <Footer />
    </>
  );
};

const Editor = () => {
  const theme = useTheme();
  return (
    <div className={"app"}>
      <div className={"component"}>
        <Sections config={createConfig(theme)} />
      </div>
    </div>
  );
};

export default Editor;
